import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '../../store/hooks';
import strings from '../../utils/strings';
import { CText, CLoader } from '../../components/common';
import { FiChevronLeft, FiChevronRight, FiFilter, FiSearch } from 'react-icons/fi';
import { searchCompanies, getAllCompanyNames, CompanyDetails, CompanySearchResult } from '../../api/copilot';

const Market: React.FC = () => {
  const navigate = useNavigate();
  const colors = useAppSelector((state) => state.theme.theme);

  // Companies state (using MCP API)
  const [filterCriteria, setFilterCriteria] = useState('');
  const [searchResults, setSearchResults] = useState<CompanyDetails[]>([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchError, setSearchError] = useState<string | null>(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasNextPage, setHasNextPage] = useState(false);
  const pageSize = 20;

  // Company search with autocomplete
  const [companySearch, setCompanySearch] = useState('');
  const [allCompanies, setAllCompanies] = useState<string[]>([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const [companiesLoading, setCompaniesLoading] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Load all companies when "All Companies" tab is selected
  const loadAllCompanies = async (page: number = 1, criteria: string = '') => {
    setSearchLoading(true);
    setSearchError(null);
    try {
      const result: CompanySearchResult = await searchCompanies(criteria, page, pageSize);
      if (result.status === 'success' && result.data) {
        setSearchResults(result.data);
        setHasNextPage(result.pagination?.has_next || false);
        setCurrentPage(page);
      } else {
        setSearchError(result.error || 'Failed to load companies');
      }
    } catch (err: any) {
      console.error('Error searching companies:', err);
      setSearchError(err.message || 'Failed to search companies');
    } finally {
      setSearchLoading(false);
    }
  };

  // Load companies on mount
  useEffect(() => {
    loadAllCompanies(1, filterCriteria);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch all company names for autocomplete
  useEffect(() => {
    const fetchCompanies = async () => {
      setCompaniesLoading(true);
      const names = await getAllCompanyNames();
      setAllCompanies(names);
      setCompaniesLoading(false);
    };
    fetchCompanies();
  }, []);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleSearch = () => {
    setCurrentPage(1);
    loadAllCompanies(1, filterCriteria);
  };

  const handlePrevPage = () => {
    if (currentPage > 1) {
      loadAllCompanies(currentPage - 1, filterCriteria);
    }
  };

  const handleNextPage = () => {
    if (hasNextPage) {
      loadAllCompanies(currentPage + 1, filterCriteria);
    }
  };

  const formatValue = (value: any): string => {
    if (value === null || value === undefined || value === '-') return '-';
    if (typeof value === 'number') {
      if (value >= 10000000) return `${(value / 10000000).toFixed(2)} Cr`;
      if (value >= 100000) return `${(value / 100000).toFixed(2)} L`;
      return value.toLocaleString();
    }
    return String(value);
  };

  // Filter companies based on search input
  const filteredCompanies = companySearch.trim()
    ? allCompanies.filter(company =>
        company.toLowerCase().includes(companySearch.toLowerCase())
      ).slice(0, 10)
    : [];

  // Navigate to company page on selection
  const handleCompanySelect = (companyName: string) => {
    setShowDropdown(false);
    setCompanySearch('');
    navigate(`/company/${encodeURIComponent(companyName)}`);
  };

  // Render All Companies tab content
  const renderAllCompaniesTab = () => (
    <div>
      {/* Company Name Search with Autocomplete */}
      <div
        className="p-5 rounded-2xl mb-4 relative card-shadow"
        style={{
          backgroundColor: colors.cardBackground || colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
        }}
        ref={dropdownRef}
      >
        <div className="flex items-center gap-2 mb-2">
          {React.createElement(FiSearch as any, { size: 18, color: colors.primary })}
          <CText type="M14" color={colors.textColor}>
            Search Company
          </CText>
          {companiesLoading && (
            <span className="text-xs" style={{ color: colors.grayScale400 }}>Loading...</span>
          )}
        </div>
        <input
          type="text"
          className="w-full p-3 rounded-lg text-sm"
          style={{
            backgroundColor: colors.backgroundColor,
            border: `1px solid ${colors.borderColor}`,
            color: colors.textColor,
          }}
          placeholder="Type company name..."
          value={companySearch}
          onChange={(e) => {
            setCompanySearch(e.target.value);
            setShowDropdown(e.target.value.trim().length > 0);
          }}
          onFocus={() => companySearch.trim() && setShowDropdown(true)}
        />
        {/* Autocomplete Dropdown */}
        {showDropdown && filteredCompanies.length > 0 && (
          <div
            className="absolute left-4 right-4 mt-1 max-h-64 overflow-y-auto rounded-lg shadow-lg z-50"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            {filteredCompanies.map((company, idx) => (
              <div
                key={company}
                className="px-4 py-3 cursor-pointer transition-colors"
                style={{
                  backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                  borderBottom: idx < filteredCompanies.length - 1 ? `1px solid ${colors.borderColor}` : 'none',
                }}
                onClick={() => handleCompanySelect(company)}
                onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '20')}
                onMouseLeave={(e) =>
                  (e.currentTarget.style.backgroundColor =
                    idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground)
                }
              >
                <CText type="M14" color={colors.textColor}>
                  {company}
                </CText>
              </div>
            ))}
          </div>
        )}
        {showDropdown && companySearch.trim() && filteredCompanies.length === 0 && !companiesLoading && (
          <div
            className="absolute left-4 right-4 mt-1 p-3 rounded-lg"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
            }}
          >
            <CText type="r12" color={colors.grayScale500}>
              No companies found
            </CText>
          </div>
        )}
      </div>

      {/* Filter Input */}
      <div
        className="p-5 rounded-2xl mb-6 card-shadow"
        style={{
          backgroundColor: colors.cardBackground || colors.inputBackground,
          border: `1px solid ${colors.borderColor}`,
        }}
      >
        <div className="flex items-center gap-2 mb-3">
          {React.createElement(FiFilter as any, { size: 18, color: colors.primary })}
          <CText type="M14" color={colors.textColor}>
            Filter Companies
          </CText>
        </div>
        <div className="flex gap-3">
          <input
            type="text"
            className="flex-1 p-3 rounded-xl text-sm focus-glow transition-all duration-200"
            style={{
              backgroundColor: colors.backgroundColor,
              border: `1px solid ${colors.borderColor}`,
              color: colors.textColor,
            }}
            placeholder="e.g., market_cap > 1000000 AND sector = 'Technology'"
            value={filterCriteria}
            onChange={(e) => setFilterCriteria(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleSearch()}
          />
          <button
            className="px-6 py-3 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
            style={{
              backgroundColor: colors.primary,
              color: colors.white,
              boxShadow: colors.shadowMedium,
            }}
            onClick={handleSearch}
            disabled={searchLoading}
          >
            {searchLoading ? 'Loading...' : 'Search'}
          </button>
        </div>
        <CText type="r12" color={colors.grayScale400} style={{ marginTop: 10 }}>
          Examples: market_cap {'>'} 1000000 | sector = 'Banking' | stock_pe {'<'} 20 | ROE {'>'} 15
        </CText>
      </div>

      {/* Loading State */}
      {searchLoading && (
        <div className="flex justify-center items-center py-12">
          <CLoader />
        </div>
      )}

      {/* Error State */}
      {searchError && !searchLoading && (
        <div className="text-center py-8">
          <CText type="M14" color={colors.alertColor}>{searchError}</CText>
          <button
            className="mt-4 px-4 py-2 rounded-lg"
            style={{ backgroundColor: colors.primary, color: colors.white }}
            onClick={() => loadAllCompanies(currentPage, filterCriteria)}
          >
            Retry
          </button>
        </div>
      )}

      {/* Company Table */}
      {!searchLoading && !searchError && searchResults.length > 0 && (
        <>
          <div
            className="overflow-x-auto rounded-2xl card-shadow"
            style={{ border: `1px solid ${colors.borderColor}` }}
          >
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: `linear-gradient(135deg, ${colors.primary}, #9F03F2)` }}>
                  <th className="px-4 py-4 text-left text-white font-semibold">Company</th>
                  <th className="px-4 py-4 text-left text-white font-semibold">Sector</th>
                  <th className="px-4 py-4 text-right text-white font-semibold">Market Cap</th>
                  <th className="px-4 py-4 text-right text-white font-semibold">Price</th>
                  <th className="px-4 py-4 text-right text-white font-semibold">P/E</th>
                  <th className="px-4 py-4 text-right text-white font-semibold">ROCE</th>
                  <th className="px-4 py-4 text-right text-white font-semibold">ROE</th>
                </tr>
              </thead>
              <tbody>
                {searchResults.map((company, idx) => (
                  <tr
                    key={company.name || idx}
                    className="border-b transition-colors cursor-pointer"
                    style={{
                      backgroundColor: idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground,
                      borderColor: colors.borderColor,
                    }}
                    onClick={() => navigate(`/company/${encodeURIComponent(company.name)}`)}
                    onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = colors.primary + '10')}
                    onMouseLeave={(e) =>
                      (e.currentTarget.style.backgroundColor =
                        idx % 2 === 0 ? colors.backgroundColor : colors.inputBackground)
                    }
                  >
                    <td className="px-4 py-3">
                      <CText type="M14" color={colors.textColor}>
                        {company.name}
                      </CText>
                    </td>
                    <td className="px-4 py-3">
                      <CText type="r12" color={colors.grayScale500}>
                        {company.sector || '-'}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.market_cap)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.current_price)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.stock_pe)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.roce)}
                      </CText>
                    </td>
                    <td className="px-4 py-3 text-right">
                      <CText type="r12" color={colors.textColor}>
                        {formatValue(company.roe)}
                      </CText>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div
            className="flex items-center justify-center gap-4 mt-6 p-4 rounded-2xl card-shadow"
            style={{ backgroundColor: colors.cardBackground || colors.inputBackground }}
          >
            <button
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
              style={{
                backgroundColor: currentPage > 1 ? colors.primary : colors.grayScale400,
                color: colors.white,
                opacity: currentPage > 1 ? 1 : 0.5,
                boxShadow: currentPage > 1 ? colors.shadowMedium : 'none',
              }}
              onClick={handlePrevPage}
              disabled={currentPage <= 1 || searchLoading}
            >
              {React.createElement(FiChevronLeft as any, { size: 18 })}
              Previous
            </button>
            <div
              className="px-4 py-2 rounded-xl"
              style={{ backgroundColor: colors.inputBackground }}
            >
              <CText type="M14" color={colors.textColor}>
                Page {currentPage}
              </CText>
            </div>
            <button
              className="flex items-center gap-2 px-5 py-2.5 rounded-xl font-semibold transition-all duration-200 hover:-translate-y-0.5 active:scale-95"
              style={{
                backgroundColor: hasNextPage ? colors.primary : colors.grayScale400,
                color: colors.white,
                opacity: hasNextPage ? 1 : 0.5,
                boxShadow: hasNextPage ? colors.shadowMedium : 'none',
              }}
              onClick={handleNextPage}
              disabled={!hasNextPage || searchLoading}
            >
              Next
              {React.createElement(FiChevronRight as any, { size: 18 })}
            </button>
          </div>
        </>
      )}

      {/* Empty State */}
      {!searchLoading && !searchError && searchResults.length === 0 && (
        <div className="text-center py-12">
          <CText type="M16" color={colors.grayScale500}>
            No companies found
          </CText>
          <CText type="r14" color={colors.grayScale400} style={{ marginTop: 8 }}>
            Try adjusting your filter criteria
          </CText>
        </div>
      )}
    </div>
  );

  return (
    <div
      className="p-4 lg:p-8 min-h-screen pb-24"
      style={{ backgroundColor: colors.bgColor || colors.backgroundColor }}
    >
      <div className="lg:max-w-6xl lg:mx-auto animate-fade-in">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold gradient-text">{strings.market}</h1>
        </div>

        {/* Content */}
        {renderAllCompaniesTab()}
      </div>
    </div>
  );
};

export default Market;
